<template>
    <!--
        One menu drawn twice — the rail and the drawer are the same nav at two widths —
        and in two MODES: the working menu, and what lives behind the gear. A desk that
        is setting the house up is not also checking arrivals, and a settings screen
        whose menu still offers the chessboard makes somebody look for a way back that
        is not there. So /settings swaps the whole list for its own headings and one
        door out.
    -->
    <DefineNav v-slot="{ collapsed }">
        <nav class="flex flex-col gap-1" :class="collapsed ? 'px-2 py-3' : 'p-3'">
            <template v-if="inSettings">
                <!-- The way back, first and on its own: everything under it is
                     configuration, and the one thing somebody wants after it is the
                     screen they came from. -->
                <UButton
                    :to="backTarget"
                    :label="collapsed ? undefined : $t('nav.back_to_app')"
                    :title="$t('nav.back_to_app')"
                    icon="i-lucide-arrow-left"
                    color="neutral"
                    variant="ghost"
                    size="lg"
                    data-testid="nav-back-to-app"
                    :class="collapsed ? 'justify-center' : 'justify-start'"
                    @click="closeMobileMenu"
                />

                <!-- Flat and not folded. There are seven headings and nobody arrives
                     here without knowing what they came to change, so a fold would be
                     one press between a person and the answer they already had. -->
                <template v-for="group in settingsGroups" :key="group.key">
                    <span
                        v-show="!collapsed"
                        class="mt-3 pe-3 ps-3 text-xs font-medium uppercase tracking-wide text-dimmed"
                        :data-testid="`nav-group-${group.key}`"
                    >
                        {{ $t(group.labelKey) }}
                    </span>

                    <template v-for="section in group.sections" :key="section.key">
                        <NavEntry
                            v-for="item in section.items"
                            :key="item.name"
                            :item="item"
                            :collapsed="collapsed"
                            :indent="!collapsed"
                        />
                    </template>
                </template>
            </template>

            <template v-else>
                <!-- „Kam?“ — the way to any screen by typing three letters of its name,
                     which is what somebody who knows this system wants and what the
                     seven folds below cost them. Ctrl+K does the same thing without
                     the mouse; the hint is on the button so that people find out. -->
                <UButton
                    type="button"
                    :label="collapsed ? undefined : $t('nav.search')"
                    :title="`${$t('nav.search')} (${searchHint})`"
                    icon="i-lucide-search"
                    color="neutral"
                    variant="ghost"
                    size="lg"
                    data-testid="nav-search"
                    :class="collapsed ? 'justify-center' : 'justify-start'"
                    @click="openSearchFromMenu"
                >
                    <template v-if="!collapsed" #trailing>
                        <UKbd class="ms-auto" :value="searchHint" />
                    </template>
                </UButton>

                <!-- What THIS person opens, whatever the folds think the order is. It
                     stands above them because a favourite that is three presses down
                     is not a favourite — and in THEIR order, because which of six is
                     first is the whole of what a favourite is worth. Dragged with a
                     mouse, or moved with the two arrows on the row; see the script. -->
                <template v-if="favouriteItems.length">
                    <div
                        v-show="!collapsed"
                        class="mt-3 flex items-center gap-2 pe-3 ps-3"
                        data-testid="nav-favourites"
                    >
                        <span
                            class="flex-1 text-xs font-medium uppercase tracking-wide text-dimmed"
                            :title="$t('nav.favourites_reorder_hint')"
                        >
                            {{ $t('nav.favourites') }}
                        </span>
                        <button
                            type="button"
                            class="text-xs text-dimmed hover:text-highlighted"
                            :title="$t('nav.favourites_reset_hint')"
                            data-testid="nav-favourites-reset"
                            @click="resetFavourites"
                        >
                            {{ $t('nav.favourites_reset') }}
                        </button>
                    </div>

                    <NavEntry
                        v-for="(item, index) in favouriteItems"
                        v-show="!collapsed"
                        :key="`fav-${item.name}`"
                        :item="item"
                        :collapsed="false"
                        favourite
                        reorder
                        :can-move-up="index > 0"
                        :can-move-down="index < favouriteItems.length - 1"
                        :drop-edge="dropEdgeFor(index)"
                        :dragging="draggedFavourite === item.name"
                        draggable="true"
                        @move="by => nudgeFavourite(index, by)"
                        @dragstart="startFavouriteDrag(item.name, $event)"
                        @dragover.prevent="dragFavouriteOver(index, $event)"
                        @drop.prevent="dropFavourite"
                        @dragend="endFavouriteDrag"
                    />
                </template>

                <!-- A group the manifest declares to be ONE screen (`flat`) is that
                     screen, drawn as an entry. It stands above the folds because the
                     first of them is „Danes“, which is where the whole portal lands —
                     and a landing page behind a fold is a landing page nobody presses
                     twice. -->
                <NavEntry
                    v-for="item in flatItems"
                    :key="`flat-${item.name}`"
                    :item="item"
                    :collapsed="collapsed"
                />

                <!-- One fold open at a time. Seven headings with everything under them
                     showing at once is the flat list this replaces, and the whole point
                     of the grouping is that the screen holds one subject rather than
                     forty entries. -->
                <template v-for="group in foldGroups" :key="group.key">
                    <!-- The heading is a button inside a wrapper, and the wrapper exists
                         for one reason: an element carries ONE `data-testid`, and the
                         store cupboard's fold needs two — the name every fold has now
                         (`nav-group-nabava`) and the one the browser suite has been
                         pressing since the module landed (`nav-materials-fold`). The
                         open state stays on the BUTTON, which is the thing that has a
                         role to carry it; a spec that wants it reaches the button
                         through the wrapper. -->
                    <div :data-testid="`nav-group-${group.key}`">
                        <button
                            type="button"
                            class="mt-2 flex w-full items-center gap-2 rounded py-2 text-left text-sm font-medium"
                            :class="[
                                openGroup === group.key ? 'text-highlighted' : 'text-default hover:bg-elevated hover:text-highlighted',
                                collapsed ? 'justify-center px-0' : 'px-3',
                            ]"
                            :title="$t(group.labelKey)"
                            :aria-expanded="openGroup === group.key"
                            :data-testid="group.key === 'nabava' ? 'nav-materials-fold' : `nav-group-${group.key}-button`"
                            @click="toggleGroup(group.key)"
                        >
                            <UIcon :name="group.icon" class="size-5 shrink-0" />
                            <span v-if="!collapsed" class="flex-1">{{ $t(group.labelKey) }}</span>

                            <!-- A shut fold that holds the screen somebody is on says so
                                 with a dot. Without it the menu answers "you are
                                 nowhere", which is the one thing a menu must never do. -->
                            <span
                                v-if="!collapsed && openGroup !== group.key && groupActive(group)"
                                class="size-1.5 shrink-0 rounded-full bg-primary"
                            />
                            <UIcon
                                v-if="!collapsed"
                                :name="openGroup === group.key ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                                class="size-4 shrink-0 text-dimmed"
                            />
                        </button>
                    </div>

                    <template v-for="section in group.sections" :key="section.key">
                        <!-- A section with no label draws no heading: the first run of
                             a fold is its front door and stands above the headings
                             rather than inside one of them. -->
                        <span
                            v-show="!collapsed && openGroup === group.key && section.labelKey"
                            class="mt-2 pe-3 ps-6 text-xs font-medium uppercase tracking-wide text-dimmed"
                            :data-testid="`nav-section-${group.key}-${section.key}`"
                        >
                            {{ section.labelKey ? $t(section.labelKey) : '' }}
                        </span>

                        <NavEntry
                            v-for="item in section.items"
                            v-show="!collapsed && openGroup === group.key"
                            :key="item.name"
                            :item="item"
                            :collapsed="false"
                            indent
                            :legacy-testid="group.key === 'nabava' ? `nav-materials-item-${item.name}` : undefined"
                        />
                    </template>
                </template>
            </template>
        </nav>
    </DefineNav>

    <!--
        Minimised, this is a rail of icons rather than nothing at all. Hiding the menu
        outright would buy the same 200 px and cost the thing the menu is for: on a
        laptop at the desk, the chessboard and the arrivals are one press away all day,
        and a hidden menu makes them two — press to open, press to go. So the labels go
        and the icons stay, each one naming itself on hover.

        A fold has no rail form — there is no width for what hangs under it — so pressing
        one puts the menu back and opens it. See toggleGroup.
    -->
    <aside
        class="hidden h-full shrink-0 border-e border-default bg-default lg:flex lg:flex-col"
        :class="collapsed ? 'w-16' : 'w-64'"
    >
        <div
            class="flex h-16 items-center gap-2 border-b border-default"
            :class="collapsed ? 'justify-center px-2' : 'px-5'"
        >
            <UIcon name="i-lucide-hotel" class="size-6 shrink-0 text-primary" />
            <div v-if="!collapsed" class="flex min-w-0 flex-col leading-tight">
                <span class="truncate text-base font-semibold text-highlighted">Imprion PMS</span>
                <AppAbout />
            </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto">
            <ReuseNav :collapsed="collapsed" />
        </div>

        <!-- At the bottom and not in the top bar: the control belongs to the thing it
             moves, and it is the one button on this screen that must not shift when it
             is pressed. -->
        <div class="border-t border-default p-2">
            <UButton
                type="button"
                :icon="collapsed ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'"
                :label="collapsed ? undefined : $t('nav.collapse_menu')"
                :title="collapsed ? $t('nav.expand_menu') : $t('nav.collapse_menu')"
                :aria-label="collapsed ? $t('nav.expand_menu') : $t('nav.collapse_menu')"
                :aria-expanded="!collapsed"
                color="neutral"
                variant="ghost"
                size="lg"
                class="w-full"
                :class="collapsed ? 'justify-center' : 'justify-start'"
                @click="toggleCollapsed"
            />
        </div>
    </aside>

    <USlideover
        v-model:open="mobileMenuOpen"
        :ui="{ content: 'w-72 max-w-[85vw]' }"
        title="Imprion PMS"
        side="left"
    >
        <template #body>
            <ReuseNav :collapsed="false" />
        </template>
    </USlideover>
</template>

<script lang="ts" setup>
    import { createReusableTemplate } from '@vueuse/core'
    import { computed, ref, watch } from 'vue'
    import type { RouteLocationRaw } from 'vue-router'

    import AppAbout from '@/components/app/AppAbout.vue'
    import NavEntry from '@/layouts/components/NavEntry.vue'
    import { useLayout } from '@/layouts/layout'
    import { readText, useNav, writeText } from '@/libs/nav'
    import { dropTarget, nudgeTarget } from '@/libs/navFavourites'
    import type { INavGroup, INavItem } from '@/types/Nav'

    const { mobileMenuOpen, closeMobileMenu } = useLayout()

    const {
        groups, settingsGroups, isEntryActive, inSettings, activeName,
        favouriteItems, moveFavourite, resetFavourites,
        openSearch, lastAppPath, openGroupKey,
    } = useNav()

    // -- Putting the favourites in order ---------------------------------------------

    /**
     * The order is arranged HERE and stored as route names — the arithmetic is in
     * `libs/navFavourites.ts` and the saving in `libs/nav.ts`; this is the pointer and
     * the keyboard.
     *
     * Two ways, and both are needed. The drag is what somebody at a desk with a mouse
     * reaches for without being told. The two arrows on the row are the only way that
     * works on the iPad the duty manager carries — HTML5 drag events do not exist on a
     * touch screen — and the only way that works for somebody on the keyboard, who
     * tabs to the arrow and presses it.
     *
     * Native drag events rather than a library: it is one list of at most eight rows in
     * a sidebar, `vue-spec/libraries.md` approves no drag library, and a dependency for
     * this would be a dependency to keep.
     */

    /** The favourite under the pointer's thumb, by route name. '' when nothing is. */
    const draggedFavourite = ref('')

    /**
     * Where it would land, counting GAPS and not rows: 0 is above the first favourite
     * and `favouriteItems.length` is below the last. -1 is "nowhere yet".
     */
    const dropGap = ref(-1)

    const favouriteNames = (): string[] => favouriteItems.value.map(item => item.name)

    const endFavouriteDrag = () => {
        draggedFavourite.value = ''
        dropGap.value          = -1
    }

    const startFavouriteDrag = (name: string, event: DragEvent) => {
        draggedFavourite.value = name
        dropGap.value          = -1

        // Firefox starts no drag at all unless the transfer carries something, and the
        // name is the honest payload: dropped anywhere else, it is the word itself.
        event.dataTransfer?.setData('text/plain', name)

        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
    }

    /**
     * Which gap the pointer is asking for: above this row in its top half, below it in
     * its bottom half. The midpoint and not the edges, so the whole row is a target and
     * there is no dead band between two of them.
     */
    const dragFavouriteOver = (index: number, event: DragEvent) => {
        if (!draggedFavourite.value) return

        const row = (event.currentTarget as HTMLElement).getBoundingClientRect()

        dropGap.value = event.clientY > row.top + row.height / 2 ? index + 1 : index

        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    }

    const dropFavourite = () => {
        if (draggedFavourite.value && dropGap.value >= 0) {
            moveFavourite(draggedFavourite.value, dropTarget(favouriteNames(), dropGap.value))
        }

        endFavouriteDrag()
    }

    /** The line drawn in the gap, expressed as an edge of one row. */
    const dropEdgeFor = (index: number): '' | 'before' | 'after' => {
        if (!draggedFavourite.value || dropGap.value < 0) return ''

        if (dropGap.value === index) return 'before'

        // The last gap has no row under it to sit above, so it is drawn under the last
        // row instead.
        const last = favouriteItems.value.length - 1

        return dropGap.value === last + 1 && index === last ? 'after' : ''
    }

    /** One step up or down, for the arrows on the row. */
    const nudgeFavourite = (index: number, by: -1 | 1) => {
        const names  = favouriteNames()
        const target = nudgeTarget(names, index, by)

        // undefined is „there is no step to take“ — the first row asked to go up.
        if (target === undefined) return

        moveFavourite(names[index], target)
    }

    const COLLAPSED_KEY = 'pms:sidebar-collapsed'

    /**
     * Per machine and not per account: whether the menu is a rail is a fact about the
     * screen it is drawn on — a 13-inch laptop at the back office wants the width, the
     * 27-inch at the desk does not — and the same person moves between them.
     */
    const collapsed = ref(readText(COLLAPSED_KEY) === '1')

    const toggleCollapsed = () => {
        collapsed.value = !collapsed.value
        writeText(COLLAPSED_KEY, collapsed.value ? '1' : '0')
    }

    /** Puts the menu back, and remembers that it is back. */
    const expandSidebar = () => {
        if (!collapsed.value) return

        collapsed.value = false
        writeText(COLLAPSED_KEY, '0')
    }

    const [DefineNav, ReuseNav] = createReusableTemplate<{ collapsed: boolean }>()

    /** Whether any entry of this fold is the screen being read. */
    const groupActive = (group: INavGroup): boolean =>
        group.sections.some(section => section.items.some(isEntryActive))

    /**
     * A group the manifest declares to be ONE screen is drawn as that entry.
     *
     * A heading that folds open to reveal a single line is two presses to reach one
     * screen, and a chevron that promises a list and delivers an entry.
     *
     * Declared (`INavGroup.flat`) rather than counted: a fold whose every other entry
     * THIS account may not open also holds one item, and flattening that one would
     * rearrange somebody's menu for a reason that is about their permissions rather
     * than about the menu itself.
     */
    const flatItems = computed((): INavItem[] => groups.value
        .filter(group => group.flat)
        .flatMap(group => group.sections.flatMap(section => section.items)))

    /** What is left: the folds. */
    const foldGroups = computed((): INavGroup[] => groups.value.filter(group => !group.flat))

    /**
     * Which fold is open, remembered per machine.
     *
     * One at a time on purpose. The menu this replaces had five folds open at once by
     * default and forty entries under them, which is the flat list it was supposed to
     * be an improvement on.
     */
    const openGroup = ref(readText(openGroupKey))

    const toggleGroup = (key: string) => {
        // In the rail there is nothing to fold shut, so the press means "open this" —
        // and opening it means having the menu back first.
        openGroup.value = collapsed.value || openGroup.value !== key ? key : ''
        expandSidebar()
        writeText(openGroupKey, openGroup.value)
    }

    /** The screen whose fold has been opened already — see the watcher below. */
    let answered = ''

    /**
     * Arriving inside a fold from anywhere else — a link, a favourite, the palette,
     * the back button — opens it, so the highlighted entry is never hidden.
     *
     * Read from the ENTRY the screen belongs to rather than from the address, because
     * that is what a fold holds: a detail page under a register is the register's entry,
     * and `activeName` is the one thing that already knows it (`libs/nav.ts`).
     *
     * ARRIVING is the word doing the work, and it is answered ONCE per screen. The
     * filtered menu has to be watched as well — it is empty until the account and the
     * licence have arrived, and a fold that could not be found then must still open when
     * it can — but the menu is rebuilt several times on every load, as the permissions,
     * the licence and the two badges land. Until 2026-09-17 each of those rebuilds
     * re-asserted the fold, so one opened by hand while the screen was still loading was
     * shut again a moment later by a "navigation" that was nothing of the kind. Nothing
     * that leaves the screen where it is may move the menu.
     */
    watch([activeName, groups], () => {
        // Only a fold can be opened. A flat group is always visible, and letting one
        // win here would SHUT whichever fold was open the moment somebody pressed
        // „Danes“ — and leave them with a menu that had quietly collapsed itself.
        const active = foldGroups.value.find(group => groupActive(group))

        // No fold holds this screen yet — on a menu that has not been filtered against
        // the account, none holds anything. Answered when one does.
        if (!active || answered === activeName.value) return

        answered = activeName.value

        if (openGroup.value !== active.key) {
            openGroup.value = active.key
            writeText(openGroupKey, active.key)
        }
    }, { immediate: true })

    /** Where „Nazaj v aplikacijo“ leads: whatever was open before the gear was pressed. */
    const backTarget = computed((): RouteLocationRaw => lastAppPath.value || { name: 'chessboard' })

    /**
     * ⌘ on a Mac and Ctrl everywhere else. `navigator.platform` is deprecated and
     * frozen in several browsers, so the client hint is asked first and the user agent
     * string is the fallback — neither is load-bearing: the worst a wrong answer does
     * is print the other key's name on a hint, and both shortcuts are registered.
     */
    const searchHint = computed((): string => {
        const hinted = (navigator as Navigator & { userAgentData?: { platform?: string } })
            .userAgentData?.platform

        return /mac/i.test(hinted ?? navigator.userAgent) ? '⌘ K' : 'Ctrl K'
    })

    const openSearchFromMenu = () => {
        closeMobileMenu()
        openSearch()
    }
</script>
